import React, { useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Download, FileText, FileSpreadsheet, Loader2 } from 'lucide-react';
import { currentIsoWeek } from '@/lib/constants';
import { API_BASE } from '@/lib/api';
import { toast } from 'sonner';

export default function Exports() {
  const [week, setWeek] = useState(currentIsoWeek());
  const [busy, setBusy] = useState('');
  const [lastGenerated, setLastGenerated] = useState(null);

  const download = async (kind) => {
    setBusy(kind);
    try {
      const url = `${API_BASE}/exports/weekly/${kind}?week=${encodeURIComponent(week)}`;
      const resp = await fetch(url, { credentials: 'include' });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const cd = resp.headers.get('Content-Disposition') || '';
      const match = cd.match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] : `weekly_${week}.${kind}`;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setLastGenerated({ kind, when: new Date().toLocaleString(), size: blob.size });
      toast.success(`${kind.toUpperCase()} downloaded`);
    } catch (e) { toast.error(`Download failed: ${e.message}`); } finally { setBusy(''); }
  };

  return (
    <AppShell title="Weekly Exports">
      <div className="max-w-2xl space-y-4" data-testid="exports-page">
        <Card>
          <CardHeader><CardTitle className="font-display">Generate weekly report</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Week</Label>
                <Input type="week" value={week} onChange={(e) => setWeek(e.target.value)} data-testid="exports-week-selector" />
              </div>
              <div className="flex flex-col justify-end text-xs text-muted-foreground">
                {lastGenerated ? (<>Last: {lastGenerated.kind.toUpperCase()} • {lastGenerated.when} • {(lastGenerated.size / 1024).toFixed(1)} KB</>) : 'No export generated yet.'}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => download('pdf')} disabled={!!busy} data-testid="exports-download-pdf-button">
                {busy === 'pdf' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}Download PDF
              </Button>
              <Button variant="secondary" onClick={() => download('xlsx')} disabled={!!busy} data-testid="exports-download-xlsx-button">
                {busy === 'xlsx' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}Download Excel
              </Button>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="font-display text-base">What’s inside</CardTitle></CardHeader>
          <CardContent className="text-sm text-muted-foreground space-y-2">
            <div>• Per-user completed / pending tasks, hours, and blockers</div>
            <div>• Company-wide totals</div>
            <div>• Expenses logged during the week</div>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
